import { forwardRef } from 'react'
import type { ReactElement } from 'react'

import {
  AlertDialog,
  Badge,
  Button,
  ChannelAvatar,
  Text,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import styles from './RecoveryDialog.module.css'

export type RecoveryDialogChannel = {
  id: string
  service: ChannelType
  name?: string | null
  avatar?: string | null
}

type RecoveryDialogProps = {
  open: boolean
  onRestore: () => void
  onDiscard: () => void
  channels?: RecoveryDialogChannel[]
  previewText?: string
}

const MAX_AVATARS = 9

// forwardRef so this can be rendered as a direct child of <Dialog.Portal>,
// which wraps each child in Radix's `asChild` Slot (PortalPrimitive). The
// ref is discarded — this component has no DOM root, only an AlertDialog
// provider — but accepting it silences the SlotClone warning.
export const RecoveryDialog = forwardRef<HTMLDivElement, RecoveryDialogProps>(
  (
    { open, onRestore, onDiscard, channels, previewText },
    _ref,
  ): ReactElement => {
    const channelCount = channels?.length ?? 0
    const hasChannels = channelCount > 0
    const hasPreview = hasChannels || !!previewText
    const visibleChannels = channels?.slice(0, MAX_AVATARS) ?? []
    const remainingChannels = Math.max(channelCount - MAX_AVATARS, 0)

    return (
      <AlertDialog open={open} onOpenChange={() => onDiscard()}>
        <AlertDialog.Content>
          <AlertDialog.Title>Resume Unfinished Post?</AlertDialog.Title>
          <AlertDialog.Description>
            You have an unfinished post from a previous session. Would you like
            to restore it?
          </AlertDialog.Description>
          {hasPreview && (
            <AlertDialog.Body className={styles.body}>
              {hasChannels && (
                <ul
                  className={styles.channels}
                  aria-label="Channels for the unfinished post"
                >
                  {visibleChannels.map((channel) => (
                    <li key={channel.id}>
                      <ChannelAvatar
                        channel={channel.service}
                        src={channel.avatar ?? undefined}
                        alt={channel.name ?? ''}
                        size="small"
                      />
                    </li>
                  ))}
                  {remainingChannels > 0 && (
                    <li>
                      <Badge size="large">
                        +{remainingChannels}
                        <VisuallyHidden>
                          more channels, {channelCount} total
                        </VisuallyHidden>
                      </Badge>
                    </li>
                  )}
                </ul>
              )}
              {previewText && (
                <div className={styles.preview}>
                  <Text className={styles.previewText} translate="no">
                    {previewText}
                  </Text>
                </div>
              )}
            </AlertDialog.Body>
          )}
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button variant="tertiary" onClick={onDiscard}>
                Discard
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="primary" onClick={onRestore}>
                Resume Post
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    )
  },
)
RecoveryDialog.displayName = 'RecoveryDialog'
